import { Edit, Plus, Trash } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  BackButton,
  SubmitButton,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../common/GeneralComponent";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import Select from "react-select";

const AddGroups = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [permissionsByGroup, setPermissionsByGroup] = useState({});
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      groupName: "",
      allowedPlatforms: [],
      status: "1",
      permissions: [],
      selectedGroups: [],
    },


    validationSchema: Yup.object({
      groupName: Yup.string()
        .required("Group name is required")
        .min(3, "Group name must be at least 3 characters")
        .max(50, "Group name must not exceed 50 characters")
        .matches(/^[^\d]/, "Group Name cannot start with a number")
        .matches(
          /^[a-zA-Z0-9 ]*$/,
          "Group name must not contain special characters"
        )
        .test(
          "not-only-numbers",
          "Group name cannot be only numbers",
          (value) => value && !/^\d+$/.test(value)
        ),
      status: Yup.string().required("Status is required"),
      allowedPlatforms: Yup.array().min(1, "Select at least one platform"),
      permissions: Yup.array().min(1, "Select at least one permission group"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      const data = {
        name: values.groupName,
        status: values.status,
        allowed_platforms: values.allowedPlatforms.join(","),
        permissions: values.permissions,
      };

      try {
        await fetchData("groups", "POST", data, {}, {}, navigate, "/groups");
        toast.success("Group created successfully!");
        resetForm();
        navigate("/groups");
      } catch (error) {
        toast.error(`Error: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        // const resp = await fetchData("permission", "GET");
        const resp = await fetchData("permission?all=true", "GET");

        if (resp.success) {
          const groups = resp.data.map((item) => item.group);

          setGroupOptions(groups.map((g) => ({ value: g, label: g })));

          const groupMap = {};
          resp.data.forEach((item) => {
            groupMap[item.group] = item.permissions;
          });
          setPermissionsByGroup(groupMap);
        }
      } catch (error) {
        console.error("Error fetching permissions", error);
      }
    };

    loadPermissions();
  }, []);
  const handleGroupSelect = (selectedGroups) => {
    const selectedGroupValues = selectedGroups
      ? selectedGroups.map((g) => g.value)
      : [];

    formik.setFieldValue("selectedGroups", selectedGroupValues);
    let allIds = [];
    selectedGroupValues.forEach((grp) => {
      if (permissionsByGroup[grp]) {
        allIds = [...allIds, ...permissionsByGroup[grp].map((p) => p.id)];
      }
    });

    formik.setFieldValue("permissions", allIds);
  };
  

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Group
        </h1>
        <BackButton to="/groups" text="Back" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Group Details
          </h1>
        </div>

        <div className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-4 w-full">
              <ThinTextField
                id="groupName"
                label="Group Name"
                placeHolder="Enter group name"
                type="text"
                value={formik.values.groupName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.groupName && Boolean(formik.errors.groupName)
                }
                helperText={formik.touched.groupName && formik.errors.groupName}
              />
            </div>

            <div className="flex flex-wrap gap-5">
              <div className="flex-1 min-w-[200px]">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Select Permission Groups{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Select
                  isMulti
                  name="selectedGroups"
                  options={groupOptions}
                  value={groupOptions.filter((g) =>
                    formik.values.selectedGroups.includes(g.value)
                  )}
                  onChange={handleGroupSelect}
                />
                {formik.touched.permissions && formik.errors.permissions && (
                  <p className="text-red-500 text-sm mt-1">
                    {formik.errors.permissions}
                  </p>
                )}
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Allowed Platforms <span className="text-red-500">*</span>
                </label>
                <Select
                  isMulti
                  name="allowedPlatforms"
                  options={[
                    { value: "web", label: "Web" },
                    { value: "mobile", label: "Mobile" },
                  ]}
                  value={formik.values.allowedPlatforms.map((p) => ({
                    value: p,
                    label: p.charAt(0).toUpperCase() + p.slice(1),
                  }))}
                  onChange={(selected) =>
                    formik.setFieldValue(
                      "allowedPlatforms",
                      selected ? selected.map((s) => s.value) : []
                    )
                  }
                />
                {formik.touched.allowedPlatforms &&
                  formik.errors.allowedPlatforms && (
                    <p className="text-red-500 text-sm mt-1">
                      {formik.errors.allowedPlatforms}
                    </p>
                  )}
              </div>
              <div className="flex-1 min-w-[150px]">
                <ThinDropdownWithSearch
                                label="Status"
                                id="status-dropdown"
                                options={[
                                  { value: 1, label: "Active" },
                                  { value: 0, label: "In-Active" },
                                ]}
                                value={formik.values.status}
                                onChange={(value) =>
                                  formik.setFieldValue("status", Number(value))
                                }
                                error={formik.touched.status && Boolean(formik.errors.status)}
                                helperText={formik.touched.status && formik.errors.status}
                              />
              </div>
            </div>
            <div className="mt-5">
              <SubmitButton
                type="submit"
                text="Submit"
                isLoading={isSubmitting}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddGroups;
